import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EntityHighlightPlugin } from './plugins/EntityHighlightPlugin';
import { $getRoot, $createParagraphNode, $createTextNode, LexicalEditor } from 'lexical';
import type { Entity } from '../types/entity';

interface LexicalMessageRendererProps {
  initialText: string;
  entities?: Entity[];
}

const theme = {
  // Add custom theme if needed
};

// Use react-error-boundary for Lexical error boundary
import { ErrorBoundary } from 'react-error-boundary';

function LexicalErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary fallbackRender={({ error }) => <div>Something went wrong: {error.message}</div>}>
      {children}
    </ErrorBoundary>
  );
}

export const LexicalMessageRenderer: React.FC<LexicalMessageRendererProps> = ({ initialText, entities }) => {
  // Defensive: filter out invalid entities
  const safeEntities = (entities || []).filter(
    e =>
      typeof e.start === 'number' &&
      typeof e.end === 'number' &&
      e.start >= 0 &&
      e.end <= initialText.length &&
      e.start < e.end
  );
  // Log for debugging
  if (entities && entities.length > 0) {
    console.log('[LexicalMessageRenderer] initialText:', initialText, 'entities:', entities, 'safeEntities:', safeEntities);
  }

  const initialConfig = {
    namespace: 'ChatMessage',
    theme,
    onError: (error: Error) => {
      throw error;
    },
    editable: false,
    editorState: (editor: LexicalEditor) => {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        const textNode = $createTextNode(initialText);
        paragraph.append(textNode);
        root.append(paragraph);
      });
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <RichTextPlugin
        contentEditable={<ContentEditable className="min-h-0" />}
        placeholder={null}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HistoryPlugin />
      <EntityHighlightPlugin entities={safeEntities} />
      {/* Add more plugins as needed */}
      <OnChangePlugin onChange={() => {}} />
    </LexicalComposer>
  );
};

export default LexicalMessageRenderer;
